import {
	checkCookieExists,
	clearCookie,
	getItemFromSessionStorage,
	removeItemFromSessionStorage,
	setItemInSessionStorage
} from '@src/utils/web-storage';
import { flattenQueryParam, includesRequiredQueryParams } from '@src/utils/request';
import { RawLocation, Route } from 'vue-router';
import { app } from '@store/modules/app';
import { getNewAccessToken } from '@src/api/login';
import { identifyMixpanel } from '@utils/analytics';
import { investmentEntity } from '@store/modules/investment-entity';
import { TokenScope } from 'types/security';
import { user } from '@store/modules/user';

const includesVerifyRoute = (pathString: string): boolean => {
	return ['/signup/verify-signup-checkout', '/signup/verify-email'].some((element) => pathString.includes(element));
};

export const refreshTokenAndRedirect = async (next: (to?: RawLocation) => void, redirectTo: Route): Promise<void> => {
	try {
		const refreshToken = await getNewAccessToken();
		await app.setUserAuthData(refreshToken);

		if (!user.user) {
			await user.getUser();
		}

		if (!refreshToken.scope || !refreshToken.access_token) {
			setItemInSessionStorage('login-redirect', redirectTo.fullPath);
			next({ name: 'login' });
		} else {
			next(redirectTo.fullPath);
		}
	} catch {
		app.SET_OAUTH_TOKEN('');
		app.UPDATE_TOKEN_SCOPE('UNAUTHENTICATED');
		clearCookie('userLoggedIn');
		setItemInSessionStorage('login-redirect', redirectTo.fullPath);

		next();
	}
};

const handleThirdPartyScope = async (
	next: (to?: RawLocation) => void,
	minTokenScopeRequired: TokenScope
): Promise<void> => {
	if (minTokenScopeRequired === 'THIRD_PARTY_ACCESS') {
		if (!user.user) {
			await user.getUser();
		}
		next();
	} else {
		next({ name: 'forbidden' });
	}
};

const handleTwoFactor = async (next: (to?: RawLocation) => void, to: Route): Promise<void> => {
	if (!to.matched.some((route) => route.name?.includes('login-two-factor'))) {
		next({ name: 'login-two-factor' });
	} else {
		next();
	}
};

const handlePrivilegedAccess = async (
	next: (to?: RawLocation) => void,
	tokenScope: TokenScope,
	nextRoute: Route,
	cancelRoute: Route
): Promise<void> => {
	const passwordVerified = tokenScope === 'PRIVILEGED_ACCESS';
	if (!passwordVerified) {
		setItemInSessionStorage('verify-password-redirect', nextRoute.name as string);
		setItemInSessionStorage(
			'verify-password-cancel',
			(cancelRoute.name as string) || 'profile-personal-information-view'
		);
		next({ name: 'password-verify' });
	} else {
		next();
	}
};

const handleInvestmentEntityQuery = async (next: (to?: RawLocation) => void, to: Route) => {
	const investmentEntityId = flattenQueryParam(to.query.investmentEntityId) ?? '';
	const entityList = user.user?.entityDetails.investmentEntities ?? [];
	const toRouteName = to.name ?? '';

	try {
		if (investmentEntityId === 'select') {
			if (entityList.length > 1) {
				next({ name: 'investment-entity-selector', params: { redirectRoute: toRouteName } });
			} else {
				next({ name: to.name ?? 'account-overview' });
			}
		} else if (investmentEntityId !== investmentEntity.investmentEntityId) {
			await user.updateSelectedEntityWithoutReload({
				investmentEntityId,
				nextRoute: { route: { name: toRouteName }, next }
			});
		} else {
			next({ name: toRouteName ?? 'account-overview' });
		}
	} catch (e) {
		next({ name: 'forbidden' });
	}
};

const handleLoginRedirect = async (next: (to?: RawLocation) => void, loginRedirect: string): Promise<void> => {
	// Check if the route requires entity selection after login (for the IR team macros)
	const routesThatRequireEntitySelectionPostLogin = [
		'/account/investment-accounts/bank-accounts/manage',
		'/account/investment-accounts/personal-information/manage',
		'/account/investment-accounts/advanced-options/view',
		'/account/investment-accounts/recurring-investment-settings/manage',
		'/account/investment-accounts/recurring-investment-settings/view',
		'/account/investment-accounts/ria-dividend-reinvestment/manage',
		'/account/investment-accounts/dividend-reinvestment/manage',
		'/account/investment-accounts/investment-plan/view',
		'/account/investment-accounts/investment-goal/manage'
	];
	const entityList = user.user?.entityDetails.investmentEntities ?? [];
	const loginRedirectRequiresEntitySelection =
		entityList.length > 1 &&
		routesThatRequireEntitySelectionPostLogin.some((element) => loginRedirect.includes(element));
	removeItemFromSessionStorage('login-redirect');
	if (loginRedirectRequiresEntitySelection) {
		next({ path: loginRedirect, query: { investmentEntityId: 'select' } });
	} else {
		next({ path: loginRedirect });
	}
};

const handleAuthenticatedScope = async (
	next: (to?: RawLocation) => void,
	to: Route,
	from: Route,
	minTokenScopeRequired: TokenScope
): Promise<void> => {
	const loginRedirect = getItemFromSessionStorage('login-redirect');
	const validLoginRedirectRoute = loginRedirect && to.name !== 'security-questions' && !to.name?.includes('login-');
	const isUnverifiedRoute = !app.isVerified && minTokenScopeRequired === 'UNVERIFIED';
	let loginRedirectIncludesVerifyRoute = false;

	if (loginRedirect) {
		loginRedirectIncludesVerifyRoute = includesVerifyRoute(loginRedirect);
	}
	const userNeedsToVerifyEmail =
		!app.isVerified && minTokenScopeRequired !== 'UNVERIFIED' && !loginRedirectIncludesVerifyRoute;
	if (!user.user) {
		await user.getUser();
	}
	const securityQuestionsRequired =
		!user.user?.additionalAuthFactorsAnswered &&
		to.name !== 'security-questions' &&
		to.name !== 'login-mobile-verify-select' &&
		!loginRedirectIncludesVerifyRoute &&
		(from.name === 'login' ||
			from.name === 'login-two-factor-confirm' ||
			from.name === 'login-mobile-verify-confirm');
	const mobileVerifyRequired =
		user.user?.shouldPromptUserForMobileVerify &&
		to.name !== 'login-mobile-verify-select' &&
		!loginRedirectIncludesVerifyRoute &&
		(from.name === 'login' || from.name === 'login-two-factor-confirm');

	if (isUnverifiedRoute) {
		next();
	} else if (userNeedsToVerifyEmail) {
		next({ name: 'unverified-email' });
	} else if (mobileVerifyRequired || (mobileVerifyRequired && securityQuestionsRequired)) {
		next({ name: 'login-mobile-verify' });
	} else if (securityQuestionsRequired) {
		next({ name: 'security-questions' });
	} else if (loginRedirect && validLoginRedirectRoute) {
		await handleLoginRedirect(next, loginRedirect);
	} else {
		const includesInvestmentEntityQuery = includesRequiredQueryParams(['investmentEntityId'], to);

		if (includesInvestmentEntityQuery) {
			await handleInvestmentEntityQuery(next, to);
		} else {
			next();
		}
	}
};

export const handleAuthentication = async (to: Route, from: Route, next: (to?: RawLocation) => void) => {
	const { isAuthenticated, tokenScope } = app;
	const minTokenScopeRequired = to.meta?.minTokenScopeRequired;
	const userHasRefreshToken = checkCookieExists('userLoggedIn');

	app.UPDATE_ROUTE_TRANSITION({ to, from });

	if (!isAuthenticated && userHasRefreshToken && tokenScope !== 'REQUIRES_ADDITIONAL_AUTH') {
		await refreshTokenAndRedirect(next, to);
	} else if (minTokenScopeRequired === 'UNAUTHENTICATED') {
		next();
	} else if (tokenScope === 'THIRD_PARTY_ACCESS') {
		await handleThirdPartyScope(next, minTokenScopeRequired as TokenScope);
	} else if (tokenScope === 'REQUIRES_ADDITIONAL_AUTH') {
		await handleTwoFactor(next, to);
	} else if (minTokenScopeRequired === 'PRIVILEGED_ACCESS') {
		await handlePrivilegedAccess(next, tokenScope, to, from);
	} else {
		if (isAuthenticated) {
			await handleAuthenticatedScope(next, to, from, minTokenScopeRequired as TokenScope);
		} else {
			if (includesVerifyRoute(to.fullPath)) {
				app.ADD_TOAST({ type: 'info', message: 'Please log in to verify your e-mail address.' });
			}

			setItemInSessionStorage('login-redirect', to.fullPath);
			next({ name: 'login' });
		}
	}

	if (isAuthenticated) {
		const mixpanelId = `${user.user?.mixpanelId}`;
		await identifyMixpanel(mixpanelId);
	}
};
